import React from 'react'
import PropTypes from 'prop-types'
import {Grid, GridItem, Text, useMultiStyleConfig, Box} from '@chakra-ui/react'
import {MinusIcon} from '../../icons'

const ProductCompareGrid = ({
    gridData,
    isOnPDP = false,
    numOfProducts = 2,
    visibleColumns = [],
    colorlessHeaders = false,
    highlightDifferences = false,
    hideIdenticalRows = false,
    gridProps = {},
}) => {
    const styles = useMultiStyleConfig('ProductCompare', {colorlessHeaders})
    const shouldAlignRowsLeft = colorlessHeaders || isOnPDP || highlightDifferences

    const productsCount = numOfProducts

    // Filter out rows where all values are the same when hideIdenticalRows is true
    const displayedData = gridData?.filter((gridRow) => {
        if (!hideIdenticalRows) return true

        const comparableValues = (gridRow?.values || []).filter(
            (_, index) => visibleColumns?.[index] ?? true,
        )

        if (comparableValues.length <= 1) return true

        const areAllValuesTheSame = comparableValues.every((value) => value === comparableValues[0])

        return !areAllValuesTheSame
    })

    return (
        <Grid
            templateColumns={`repeat(${productsCount}, minmax(0, 1fr))`}
            {...styles.grid}
            {...gridProps}
            data-testid="product-compare-grid"
        >
            {displayedData?.map((gridRow) =>
                Array.from({length: productsCount}).map((_, productIndex) => {
                    const isColumnVisible = visibleColumns?.[productIndex] ?? true
                    const value = gridRow?.values?.[productIndex]
                    const isEmptyValue = value === '-'
                    const hasValue = value && value !== 'false' && value !== ''
                    const shouldShowRowContent = hasValue && !isEmptyValue

                    if (!isColumnVisible) {
                        return (
                            <GridItem
                                key={`${gridRow.id}-product-${productIndex}`}
                                {...styles.gridCell}
                            />
                        )
                    }

                    return (
                        <GridItem
                            key={`${gridRow.id}-product-${productIndex}`}
                            {...styles.gridCell}
                            textAlign={shouldAlignRowsLeft ? 'left' : undefined}
                        >
                            <Box width="100%" textAlign={shouldAlignRowsLeft ? 'left' : undefined}>
                                <Box
                                    display="flex"
                                    justifyContent={
                                        shouldShowRowContent
                                            ? shouldAlignRowsLeft
                                                ? 'flex-start'
                                                : 'center'
                                            : 'flex-start'
                                    }
                                >
                                    {!shouldShowRowContent && (
                                        <MinusIcon
                                            data-testid="missing-value-icon"
                                            color="foreground.neutral.dim"
                                            width="iconsSize.small"
                                            height="iconsSize.small"
                                        />
                                    )}
                                </Box>

                                <Text
                                    {...styles.gridLabel}
                                    marginBottom={1}
                                    color="foreground.neutral.default"
                                    textStyle="body-medium"
                                    fontWeight="medium"
                                    textAlign={shouldAlignRowsLeft ? 'left' : undefined}
                                    visibility={shouldShowRowContent ? 'visible' : 'hidden'}
                                    aria-hidden={!shouldShowRowContent}
                                >
                                    {shouldShowRowContent ? gridRow?.label : '\u00A0'}
                                </Text>
                                <Text
                                    wordBreak={'break-word'}
                                    whiteSpace={'pre-line'}
                                    textOverflow={'ellipsis'}
                                    {...styles.productsTypo}
                                    color="foreground.neutral.muted"
                                    textStyle="body-medium"
                                    textAlign={shouldAlignRowsLeft ? 'left' : undefined}
                                    visibility={shouldShowRowContent ? 'visible' : 'hidden'}
                                    aria-hidden={!shouldShowRowContent}
                                >
                                    {shouldShowRowContent ? value : '\u00A0'}
                                </Text>
                            </Box>
                        </GridItem>
                    )
                }),
            )}
        </Grid>
    )
}

ProductCompareGrid.propTypes = {
    gridData: PropTypes.array,
    isOnPDP: PropTypes.bool,
    numOfProducts: PropTypes.number,
    visibleColumns: PropTypes.arrayOf(PropTypes.bool),
    colorlessHeaders: PropTypes.bool,
    highlightDifferences: PropTypes.bool,
    hideIdenticalRows: PropTypes.bool,
    gridProps: PropTypes.object,
}

export default ProductCompareGrid
